// Mode tactics (§10.4): Hot Potato (carrier hunts the nearest non-immune bean; everyone else keeps
// away, further as the fuse heats up), Crown Grab (chase the holder, catch popped crowns where they
// will land; the holder runs for safe, empty ground and shoves anyone close), ghosts (hover over
// the most exposed bean and drop pebbles on edge moments), the Lobby playground and the Podium
// brawl, plus the two flavor goals: a victory dance near the edge and Gary's teacup ride.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { BS, EK, ItemKind, MapId, ModeId, type Personality } from '../ids';
import { MAPS } from '../maps/index';
import { chance, rand, randIntRange, weightedIndex } from '../rng';
import { hasBody } from '../sim/bean';
import { ghostReady } from '../sim/ghosts';
import type { Entity, Vec2 } from '../types';
import { Goal } from './brain';
import { gauss, grounded, type Ctx } from './context';
import { consider, othersTargeting, type Choice } from './goals';
import { wantDir, wantPoint } from './motor';
import { bestSafeSpot, crownCaution, findEntity, hittable, isEnemy, lungeOk, nearestEnemy, pathHazard, pushDir, safePoint, shoveAt, wander } from './tactics';
import { cakeAnchorAway, cakeTravel, spinGapSpot } from './mapTactics';
import { BOT_TUNE } from './tuning';
import { dcos, dsin } from '../sim/dmath';

const T = TUNE.bot.tactics;
const MT = BOT_TUNE.modes;
const P: Vec2 = { x: 0, y: 0 };
const SP: Vec2 = { x: 0, y: 0 };

// ---- Hot Potato ---------------------------------------------------------------------------------

export function potatoKeepAway(ctx: Ctx): number {
  return T.potatoKeepAway + ctx.w.mode.potatoIntensity * MT.potatoKeepAwayPerIntensity;
}

/** Potato options (§10.4); returns the multiplier for ordinary ATTACK utilities this think. */
export function potatoOptions(ctx: Ctx, c: Choice): number {
  const w = ctx.w;
  const b = ctx.self;
  const carrier = w.mode.potatoSlot;
  if (carrier === NO_SLOT || !w.beans[carrier].active) return 1;
  if (carrier === b.slot) {
    for (let s = 0; s < ctx.seen.length; s++) {
      if (s === b.slot || !isEnemy(ctx, s)) continue;
      const o = ctx.seen[s];
      const t = w.beans[s];
      if (!o.body || !hasBody(t) || t.potatoImmuneTicks > 0 || ctx.w.tick < ctx.brain.unreachableUntil[s]) continue;
      const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
      const prox = Math.max(MT.potatoProxMin, 1 - d / MT.potatoProxRange);
      const giver = s === w.mode.potatoPasser ? MT.potatoGiverMult : 1;
      consider(ctx, c, Goal.POTATO_PASS, s, 0, MT.potatoPassU * prox * giver);
    }
    return MT.potatoCarrierAttackMult;
  }
  const o = ctx.seen[carrier];
  const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
  const keep = potatoKeepAway(ctx);
  if (d < keep + MT.potatoFleeBuffer) consider(ctx, c, Goal.POTATO_FLEE, NO_SLOT, 0, MT.potatoFleeU * (1 + Math.max(0, (keep - d) / keep)));
  return MT.potatoAttackMult;
}

/** Carrier: run the target down (contact or a shove passes the bomb). */
export function potatoPass(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const slot = br.target;
  if (slot === NO_SLOT || !b.hasPotato) {
    br.forceThink = true;
    wander(ctx);
    return;
  }
  const o = ctx.seen[slot];
  if (!o.body || ctx.w.beans[slot].potatoImmuneTicks > 0) {
    br.forceThink = true;
    wander(ctx);
    return;
  }
  const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
  if (b.cdShove === 0 && b.state === BS.NORMAL && d < MT.potatoShoveRange && hittable(ctx, slot) && lungeOk(ctx, o.x - b.pos.x, o.y - b.pos.y, Math.max(1e-6, d))) {
    shoveAt(ctx, o, o.x, o.y);
    return;
  }
  const lead = Math.min(MT.potatoLeadMaxSec, d / (TUNE.bean.runSpeed * TUNE.potato.carrierSpeed));
  const tx = o.x + o.vx * lead;
  const ty = o.y + o.vy * lead;
  // Crumble Cake: the victim sits on its own island; hop over to it (or pick someone reachable).
  if (ctx.w.cfg.mapId === MapId.CAKE) {
    if (cakeTravel(ctx, tx, ty, 1)) {
      br.want.contact = slot;
      return;
    }
    br.unreachableUntil[slot] = ctx.w.tick + MT.potatoUnreachableTicks;
    br.forceThink = true;
  }
  wantPoint(ctx, tx, ty, 1);
  br.want.contact = slot;
  br.want.arrive = MT.potatoArrive;
  br.want.margin = ctx.margin * MT.potatoCarrierMarginMult;
}

/** Non-carrier: keep away from the bomb, preferring safe ground (§10.4). */
export function potatoFlee(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const carrier = w.mode.potatoSlot;
  if (carrier === NO_SLOT || carrier === b.slot) {
    br.forceThink = true;
    wander(ctx);
    return;
  }
  const o = ctx.seen[carrier];
  if (w.cfg.mapId === MapId.CAKE && MT.potatoCakeAnchorFlee && cakeAnchorAway(ctx, o.x, o.y, SP) && cakeTravel(ctx, SP.x, SP.y, 1)) return;
  if (w.tick - br.pointTick >= MT.fleeRepickTicks) {
    const keep = potatoKeepAway(ctx);
    const cx = o.x + o.vx * MT.fleePredictSec;
    const cy = o.y + o.vy * MT.fleePredictSec;
    let best = -1e9;
    const n = MT.fleeCandidates;
    const base = rand(br) * Math.PI * 2;
    for (let i = 0; i <= n; i++) {
      const a = base + (i / n) * Math.PI * 2;
      const r = i === n ? 0 : MT.fleeStep;
      const x = b.pos.x + dcos(a) * r;
      const y = b.pos.y + dsin(a) * r;
      const s = ctx.field.safety(x, y);
      if (s < ctx.margin * MT.fleeMinSafetyFrac) continue;
      const score = Math.min(Math.hypot(x - cx, y - cy), keep + MT.fleeDistCap) * MT.fleeDistWeight
        + Math.min(s, ctx.margin + MT.fleeSafetyCap) * MT.fleeSafetyWeight
        - ctx.hz.danger(x, y, b.radius, true) * MT.fleeDangerWeight;
      if (score > best) {
        best = score;
        br.gx = x;
        br.gy = y;
      }
    }
    br.pointTick = w.tick;
  }
  wantPoint(ctx, br.gx, br.gy, 1);
  br.want.arrive = MT.fleeArrive;
}

// ---- Crown Grab ---------------------------------------------------------------------------------

const LAND: Vec2 = { x: 0, y: 0 };

/** Where a loose crown will be when it touches down (ballistic from its velocity, §10.4). */
export function crownLanding(ctx: Ctx, e: Entity, out: Vec2): Vec2 {
  const g = ctx.w.rules.gravity;
  const ground = Math.max(0, MAPS[ctx.w.cfg.mapId].groundAt(ctx.w, e.pos.x, e.pos.y));
  const h = e.z - ground;
  let t = 0;
  if (h > 1e-3 || e.vz > 0) {
    // z + vz t − ½ g t² = 0 → t = (vz + √(vz² + 2 g h)) / g
    t = (e.vz + Math.sqrt(Math.max(0, e.vz * e.vz + 2 * g * Math.max(0, h)))) / g;
  }
  out.x = e.pos.x + e.vel.x * t;
  out.y = e.pos.y + e.vel.y * t;
  return out;
}

function crownEntity(ctx: Ctx): Entity | null {
  const id = ctx.w.mode.crownEntId;
  if (id === 0) return null;
  const e = findEntity(ctx.w, id);
  return e && e.kind === EK.CROWN ? e : null;
}

/**
 * How a personality plays Crown Grab (§10.5 drives the weights): a Coward is a slippery wearer
 * that runs early and only shoves point-blank, a Bully or Chaos Goblin stands its ground and shoves
 * anyone close, an Opportunist vultures close to the wearer for the pop. `threat` scales how hard
 * the wearer runs from chasers, `shove` is its shove range (0 never), `loiter` the vulture ring.
 */
export function crownStyle(p: Personality): { threat: number; shove: number; loiter: number } {
  return MT.crownStyle[p] ?? MT.crownStyle[0];
}

/**
 * Crown options (§10.4); returns the multiplier for ordinary ATTACK utilities. The holder holds;
 * one or two bots chase it (per map, within the max-2 rule), a fresh wearer still in the crowd is
 * mobbed and the one who just lost it wants it back; everyone else circles at a distance ready to
 * catch a popped crown instead of crowding the chase; a loose crown is fetched where it will land.
 */
export function crownOptions(ctx: Ctx, c: Choice): number {
  const w = ctx.w;
  const b = ctx.self;
  const holder = w.mode.crownHolder;
  if (holder === b.slot || b.holdsCrown) {
    consider(ctx, c, Goal.CROWN_HOLD, NO_SLOT, 0, MT.crownHoldU);
    return MT.crownHolderAttackMult;
  }
  const e = crownEntity(ctx);
  if (holder !== NO_SLOT && w.beans[holder].active) {
    if (!isEnemy(ctx, holder)) return MT.crownEscortAttackMult;
    const o = ctx.seen[holder];
    if (!o.body) return 1;
    const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
    const prox = Math.max(TUNE.bot.attack.proximityMin, 1 - d / TUNE.bot.attack.proximityRange);
    const share = Math.min(1, w.beans[holder].crownTicks / TUNE.crown.goalTicks);
    // The one it was just taken from wants it back, right now (a rematch, not a crowd).
    const revenge = w.tick - ctx.brain.crownLostTick < MT.crownRevengeTicks ? MT.crownRevengeMult : 1;
    // A fresh wearer still in the crowd gets mobbed (the scramble); one that got away is hunted.
    const mob = w.tick - ctx.brain.crownPickTick < (MT.crownFreshTicks[w.cfg.mapId] ?? 90) && d < (MT.crownMobRange[w.cfg.mapId] ?? 3.5) ? MT.crownMobMult : 1;
    // A per-map cap on hunters (`crownChasers`): more and the crown time spreads too thin to win.
    const cap = MT.crownChasers[w.cfg.mapId] ?? TUNE.bot.maxPerTarget;
    if (othersTargeting(ctx, holder, ctx.brain) < cap) {
      consider(ctx, c, Goal.ATTACK, holder, 0, MT.crownChaseU * ctx.brain.p.aggression * prox * (1 + share * MT.crownLeaderBoost) * revenge * mob);
    }
    if (e) consider(ctx, c, Goal.CROWN_FETCH, NO_SLOT, e.id, MT.crownLoiterU);
    return MT.crownOtherAttackMult;
  }
  if (!e) return 1;
  crownLanding(ctx, e, LAND);
  if (MAPS[w.cfg.mapId].groundAt(w, LAND.x, LAND.y) === VOID) return 1;
  const d = Math.hypot(LAND.x - b.pos.x, LAND.y - b.pos.y);
  const mine = ctx.brain.crownPoppedBy === b.slot ? MT.crownRegrabMult : 1;
  consider(ctx, c, Goal.CROWN_FETCH, NO_SLOT, e.id, MT.crownFetchU * mine * Math.max(MT.crownFetchMin, 1 - d / MT.crownFetchRange));
  return MT.crownOtherAttackMult;
}

/** CROWN_FETCH: run to where a loose crown lands; while someone else wears it, loiter nearby. */
export function crownFetch(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const e = findEntity(w, brain.targetEnt);
  if (!e) {
    brain.forceThink = true;
    wander(ctx);
    return;
  }
  const holder = w.mode.crownHolder;
  if (holder !== NO_SLOT && holder !== b.slot) {
    // Vulture: keep a ring around the wearer, out of the chase, on safe ground, ready for the pop
    // (an Opportunist circles closest).
    const style = crownStyle(brain.p.personality);
    const o = ctx.seen[holder];
    if (w.tick - brain.pointTick >= MT.loiterRepickTicks) {
      let best = -1e9;
      const base = rand(brain) * Math.PI * 2;
      for (let i = 0; i < MT.loiterCandidates; i++) {
        const a = base + (i / MT.loiterCandidates) * Math.PI * 2;
        const x = o.x + dcos(a) * style.loiter;
        const y = o.y + dsin(a) * style.loiter;
        const s = ctx.field.safety(x, y);
        if (s < ctx.margin * MT.fleeMinSafetyFrac || ctx.field.isBlocked(x, y)) continue;
        let crowd = 0;
        for (let k = 0; k < ctx.seen.length; k++) {
          if (k === b.slot || k === holder || !ctx.seen[k].body) continue;
          const dd = Math.hypot(ctx.seen[k].x - x, ctx.seen[k].y - y);
          if (dd < MT.loiterCrowd) crowd += 1 - dd / MT.loiterCrowd;
        }
        const score = Math.min(s, ctx.margin + MT.fleeSafetyCap) - crowd * MT.loiterCrowdWeight - Math.hypot(x - b.pos.x, y - b.pos.y) * MT.loiterTravelWeight;
        if (score > best) {
          best = score;
          brain.gx = x;
          brain.gy = y;
        }
      }
      brain.pointTick = w.tick;
    }
    wantPoint(ctx, brain.gx, brain.gy, MT.loiterSpeed);
    brain.want.arrive = MT.holdArrive;
    return;
  }
  crownLanding(ctx, e, LAND);
  safePoint(ctx, LAND.x, LAND.y, MT.crownFetchMargin, SP);
  if (w.tick < brain.crownHesitateUntil) {
    // A beat to see where it flies: turn toward it, a half step, then go.
    const dx = SP.x - b.pos.x;
    const dy = SP.y - b.pos.y;
    const d = Math.max(1e-6, Math.hypot(dx, dy));
    wantDir(ctx, dx / d, dy / d, MT.crownHesitateStick);
    brain.want.precise = true;
    return;
  }
  // Crumble Cake: it may have come down on another island; hop over.
  if (w.cfg.mapId === MapId.CAKE && cakeTravel(ctx, SP.x, SP.y, 1)) {
    brain.want.precise = true;
    return;
  }
  wantPoint(ctx, SP.x, SP.y, 1);
  brain.want.precise = true;
  brain.want.arrive = MT.crownFetchArrive;
  brain.want.margin = MT.crownFetchMargin;
}

/**
 * Holder (§10.4): keep running for the safest, emptiest ground away from everyone (a moving target
 * is hard to line up), and shove anyone who gets within reach.
 */
export function crownHold(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  if (!b.holdsCrown && w.mode.crownHolder !== b.slot) {
    brain.forceThink = true;
    wander(ctx);
    return;
  }
  const style = crownStyle(brain.p.personality);
  const close = style.shove > 0 ? nearestEnemy(ctx, style.shove) : NO_SLOT;
  // Never in the air: an airborne lunge keeps its speed after it ends (no ground to stop on).
  if (close !== NO_SLOT && b.cdShove === 0 && b.state === BS.NORMAL && grounded(b) && hittable(ctx, close)) {
    const o = ctx.seen[close];
    // Never lunge over a rim with the crown on: a teeter pops it.
    if (lungeOk(ctx, o.x - b.pos.x, o.y - b.pos.y, Math.max(1e-6, Math.hypot(o.x - b.pos.x, o.y - b.pos.y)))) {
      shoveAt(ctx, o, o.x, o.y);
      return;
    }
  }
  // A hazard about to land on us (the ball's lane, a bubble, a candle): any hit pops the crown, so
  // the wearer gets out of the way first (EVADE never outbids holding the crown).
  if (ctx.hz.danger(b.pos.x, b.pos.y, b.radius, true) * brain.p.hazardRespect >= TUNE.bot.evade.dangerThreshold) {
    bestSafeSpot(ctx, TUNE.bot.evade.safeSearchRadius, SP);
    wantPoint(ctx, SP.x, SP.y, 1);
    brain.want.urgent = true;
    brain.want.precise = true;
    brain.pointTick = -1e9;
    return;
  }
  // Crumble Cake: hold an island far from everyone; chasers must hop over to it.
  if (w.cfg.mapId === MapId.CAKE) {
    let tx = 0;
    let ty = 0;
    let n = 0;
    for (let k = 0; k < ctx.seen.length; k++) {
      if (k === b.slot || !isEnemy(ctx, k) || !ctx.seen[k].body) continue;
      tx += ctx.seen[k].x;
      ty += ctx.seen[k].y;
      n++;
    }
    if (n > 0 && cakeAnchorAway(ctx, tx / n, ty / n, SP) && cakeTravel(ctx, SP.x, SP.y, 1)) return;
  }
  // Spin Cycle: ride the gap between the arms, as far from the chasers as it allows.
  if (spinGapSpot(ctx, MT.holdThreatWeight * style.threat * crownCaution(ctx), SP)) {
    brain.gx = SP.x;
    brain.gy = SP.y;
    brain.pointTick = w.tick;
    wantPoint(ctx, SP.x, SP.y, 1);
    brain.want.arrive = MT.holdArrive;
    return;
  }
  const caution = crownCaution(ctx);
  const arrived = Math.hypot(brain.gx - b.pos.x, brain.gy - b.pos.y) < MT.holdArrive * 2;
  if (w.tick - brain.pointTick >= MT.holdRepickTicks || arrived) {
    let best = -1e9;
    const n = MT.holdCandidates;
    const base = rand(brain) * Math.PI * 2;
    for (let ring = 0; ring < 2; ring++) {
      for (let i = 0; i < n; i++) {
        const a = base + ((i + ring * 0.5) / n) * Math.PI * 2;
        const r = ring === 0 ? MT.holdStep : MT.holdStepFar;
        const x = b.pos.x + dcos(a) * r;
        const y = b.pos.y + dsin(a) * r;
        const s = ctx.field.safety(x, y);
        if (s < ctx.margin || ctx.field.isBlocked(x, y)) continue;
        // Pinball: a run past a bumper is a launch (and a popped crown), and so is the ball's lane.
        if (ctx.field.launchOnSegment(b.pos.x, b.pos.y, x, y, b.radius, MT.holdPadClear, true)) continue;
        if (pathHazard(ctx, b.pos.x, b.pos.y, x, y, (r / (TUNE.bean.runSpeed * TUNE.crown.holderSpeed)) * 60)) continue;
        let threat = 0;
        for (let k = 0; k < ctx.seen.length; k++) {
          if (k === b.slot || !isEnemy(ctx, k) || !ctx.seen[k].body) continue;
          const o = ctx.seen[k];
          const d = Math.hypot(o.x + o.vx * MT.holdPredictSec - x, o.y + o.vy * MT.holdPredictSec - y);
          threat += 1 / Math.max(MT.holdThreatMin, d);
        }
        // Spin Cycle: by the hub an arm is a nudge, by the rim a launch.
        const hub = w.cfg.mapId === MapId.SPIN ? Math.max(0, Math.hypot(x, y) - BOT_TUNE.feel.spinWanderRamp) * MT.holdSpinHubWeight : 0;
        // Pinball and Teacup: also avoid where the ball (or a doomed wedge) will be in a couple of seconds.
        const longDanger = w.cfg.mapId === MapId.PINBALL || w.cfg.mapId === MapId.TEACUP ? MAPS[w.cfg.mapId].danger(w, x, y, MT.holdLongAheadTicks) * MT.holdLongDangerWeight : 0;
        const score = Math.min(s, ctx.margin + MT.holdSafetyCap) * MT.holdSafetyWeight - threat * MT.holdThreatWeight * style.threat * caution
          - (ctx.hz.danger(x, y, b.radius, true) * MT.holdDangerWeight + longDanger) * brain.p.hazardRespect - hub;
        if (score > best) {
          best = score;
          brain.gx = x;
          brain.gy = y;
        }
      }
    }
    brain.pointTick = w.tick;
  }
  wantPoint(ctx, brain.gx, brain.gy, 1);
  brain.want.arrive = MT.holdArrive;
}


/** Where a respawning bean should steer its parachute. */
export function respawnTarget(ctx: Ctx, out: Vec2): Vec2 {
  const e = ctx.w.cfg.modeId === ModeId.CROWN_GRAB && ctx.w.mode.crownHolder === NO_SLOT ? crownEntity(ctx) : null;
  if (e) return crownLanding(ctx, e, out);
  const holder = ctx.w.mode.crownHolder;
  if (ctx.w.cfg.modeId === ModeId.CROWN_GRAB && holder !== NO_SLOT && holder !== ctx.self.slot) {
    out.x = ctx.seen[holder].x;
    out.y = ctx.seen[holder].y;
    return safePoint(ctx, out.x, out.y, ctx.margin, out);
  }
  out.x = ctx.cx;
  out.y = ctx.cy;
  return safePoint(ctx, out.x, out.y, ctx.margin, out);
}

// ---- Ghosts -------------------------------------------------------------------------------------

/** Picks the most exposed living bean for this ghost to haunt (§10.4). */
export function ghostThink(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  let best = NO_SLOT;
  let bs = -1e9;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    if (!o.body) continue;
    const t = ctx.w.beans[s];
    const edge = o.state === BS.TEETER || o.state === BS.LEDGE ? MT.ghostEdgeBonus : 0;
    const safety = Math.max(0, Math.min(MT.ghostSafetyCap, ctx.field.safety(o.x, o.y)));
    let score = edge + (MT.ghostSafetyCap - safety) * (1 + t.dizzy / 100);
    if (s === brain.grudge) score *= MT.ghostGrudgeMult;
    if (ctx.human[s]) score *= MT.ghostHumanMult;
    score -= Math.hypot(o.x - b.pos.x, o.y - b.pos.y) * MT.ghostTravelWeight;
    if (s === brain.target) score += MT.ghostKeepBonus;
    if (score > bs) {
      bs = score;
      best = s;
    }
  }
  brain.goal = Goal.HAUNT;
  brain.target = best;
}

export function ghost(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  brain.want.margin = -99;
  brain.want.ignoreDanger = true;
  const slot = brain.target;
  if (slot === NO_SLOT || !ctx.seen[slot].body) {
    // Lazy figure eights over the arena.
    const a = w.tick * MT.ghostIdleRate + b.slot;
    wantPoint(ctx, ctx.cx + dcos(a) * MT.ghostIdleRadius, ctx.cy + dsin(2 * a) * MT.ghostIdleRadius * 0.5, MT.ghostIdleSpeed);
    return;
  }
  const o = ctx.seen[slot];
  const t = w.beans[slot];
  let hx: number;
  let hy: number;
  const edgeMoment = o.state === BS.LEDGE || o.state === BS.TEETER || o.state === BS.CLIMB;
  if (edgeMoment) {
    hx = t.edgePoint.x;
    hy = t.edgePoint.y;
  } else {
    const ahead = T.ghostDropAheadTicks / 60 * MT.ghostLeadDamp;
    hx = o.x + o.vx * ahead;
    hy = o.y + o.vy * ahead;
  }
  const ready = ghostReady(b) && b.cdPebble === 0;
  if (!ready) brain.ghostReadySince = w.tick;
  // A ghost misjudges where its victim stands from up there: one aim error per drop attempt.
  if (w.tick >= brain.ghostAimUntil) {
    const sigma = MT.ghostAimSigma[brain.p.difficulty] ?? 0.5;
    brain.ghostAimX = gauss(brain) * sigma;
    brain.ghostAimY = gauss(brain) * sigma;
    brain.ghostAimUntil = w.tick + MT.ghostAimTicks;
  }
  hx += brain.ghostAimX;
  hy += brain.ghostAimY;
  const d = Math.hypot(hx - b.pos.x, hy - b.pos.y);
  if (ready) {
    const predEdge = ctx.field.safety(hx, hy) < MT.ghostEdgeSafety;
    const dizzyNearEdge = t.dizzy >= MT.ghostDizzyDrop && ctx.field.safety(hx, hy) < MT.ghostDizzySafety;
    // A nudge into a hazard (an arm about to sweep, the ball's path) is as good as a nudge off an
    // edge; and a ghost that has waited long enough drops one just to be a nuisance.
    const intoHazard = ctx.hz.danger(hx, hy, t.radius, false) >= MT.ghostHazardDrop;
    const bored = w.tick - brain.ghostReadySince > MT.ghostImpatientTicks;
    if (d <= T.ghostDropDist && (edgeMoment || predEdge || dizzyNearEdge || intoHazard || bored)) {
      brain.press.shove = true;
      brain.press.sx = 0;
      brain.press.sy = 0;
      brain.ghostAimUntil = 0;
    }
    wantPoint(ctx, hx, hy, 1);
    brain.want.arrive = MT.ghostArrive;
    brain.want.precise = true;
    return;
  }
  // Reloading: circle above the victim like a bored vulture.
  const a = w.tick * MT.ghostCircleRate + b.slot * 1.7;
  wantPoint(ctx, hx + dcos(a) * MT.ghostCircleRadius, hy + dsin(a) * MT.ghostCircleRadius, MT.ghostCircleSpeed);
}

// ---- Lobby ---------------------------------------------------------------------------------------

export const LobbyTask = { STROLL: 0, DUMMY: 1, VEND: 2, READY: 3, SOCIAL: 4, SPAR: 5, CARD: 6 } as const;

/** Lobby options (§10.4): rotate between playground activities. */
export function lobbyOptions(ctx: Ctx, c: Choice): number {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const L = MT.lobby;
  if (w.tick >= brain.lobbyUntil) {
    let dummies = 0;
    for (const e of w.ents) if (e.kind === EK.DUMMY) dummies++;
    const weights = [
      L.strollWeight,
      dummies > 0 ? L.dummyWeight : 0,
      b.item === ItemKind.NONE && b.cdVend === 0 ? L.vendWeight : 0,
      L.readyWeight,
      ctx.bc.humanSlots.length > 0 ? L.socialWeight : 0,
      L.sparWeight * brain.p.aggression,
      L.cardWeight,
    ];
    brain.lobbyTask = Math.max(0, weightedIndex(brain, weights));
    brain.lobbyUntil = w.tick + randIntRange(brain, L.taskMinTicks, L.taskMaxTicks);
    brain.pointTick = -1e9;
  }
  if (brain.lobbyTask === LobbyTask.SPAR) {
    const s = sparPartner(ctx, L.sparRange);
    if (s !== NO_SLOT) consider(ctx, c, Goal.ATTACK, s, 0, L.sparU);
  }
  consider(ctx, c, Goal.LOBBY, NO_SLOT, 0, L.taskU);
  return L.attackMult;
}

/**
 * The nearest bean within `range` up for a play fight: another bot, or a human who has been
 * throwing shoves lately. A human just standing in the lobby (reading the room, picking a card)
 * is left alone.
 */
function sparPartner(ctx: Ctx, range: number): number {
  const b = ctx.self;
  let best = NO_SLOT;
  let bd = range;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s) || !ctx.seen[s].body) continue;
    if (ctx.human[s] && ctx.w.tick - ctx.per.lungeTick[s] > MT.lobby.humanSparTicks) continue;
    const d = Math.hypot(ctx.seen[s].x - b.pos.x, ctx.seen[s].y - b.pos.y);
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

function nearestDummy(ctx: Ctx): Entity | null {
  const b = ctx.self;
  let best: Entity | null = null;
  let bd = 1e9;
  for (const e of ctx.w.ents) {
    if (e.kind !== EK.DUMMY) continue;
    const d = Math.hypot(e.pos.x - b.pos.x, e.pos.y - b.pos.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
}

export function lobby(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const L = MT.lobby;
  brain.waiting = false;
  switch (brain.lobbyTask) {
    case LobbyTask.DUMMY: {
      const e = nearestDummy(ctx);
      if (!e) break;
      const dx = e.pos.x - b.pos.x;
      const dy = e.pos.y - b.pos.y;
      const d = Math.hypot(dx, dy);
      if (d < L.dummyShoveRange && b.cdShove === 0 && b.state === BS.NORMAL) {
        brain.press.shove = true;
        brain.press.noisy = true;
        brain.press.sx = dx;
        brain.press.sy = dy;
        return;
      }
      // Line up from the side away from the lid's edge.
      pushDir(ctx, e.pos.x, e.pos.y, b.pos.x, b.pos.y, P);
      safePoint(ctx, e.pos.x - P.x * L.dummyStandoff, e.pos.y - P.y * L.dummyStandoff, ctx.margin, SP);
      wantPoint(ctx, SP.x, SP.y, L.walkSpeed);
      return;
    }
    case LobbyTask.VEND:
      if (b.item !== ItemKind.NONE) {
        brain.lobbyUntil = w.tick;
        break;
      }
      wantPoint(ctx, L.vend.x, L.vend.y, L.walkSpeed);
      brain.want.precise = true;
      brain.want.margin = 0;
      return;
    case LobbyTask.READY:
    case LobbyTask.CARD: {
      const spot = brain.lobbyTask === LobbyTask.READY ? L.readyPad : L.cards[b.slot % L.cards.length];
      const d = Math.hypot(spot.x - b.pos.x, spot.y - b.pos.y);
      wantPoint(ctx, spot.x, spot.y, L.walkSpeed);
      brain.want.precise = true;
      brain.want.arrive = L.padArrive;
      brain.waiting = d < L.padArrive;
      return;
    }
    case LobbyTask.SOCIAL: {
      const humans = ctx.bc.humanSlots;
      let h = NO_SLOT;
      for (const s of humans) if (s !== b.slot && ctx.seen[s].body) h = s;
      if (h === NO_SLOT) break;
      const o = ctx.seen[h];
      const dx = b.pos.x - o.x;
      const dy = b.pos.y - o.y;
      const d = Math.max(1e-6, Math.hypot(dx, dy));
      wantPoint(ctx, o.x + (dx / d) * L.socialDist, o.y + (dy / d) * L.socialDist, L.walkSpeed);
      if (d < L.socialDist + L.socialSlack && brain.emoteAt < 0 && chance(brain, L.socialEmoteChance)) brain.emoteAt = w.tick + randIntRange(brain, 5, 30);
      return;
    }
    default:
      break;
  }
  wander(ctx);
  brain.want.speed = L.walkSpeed;
}

// ---- Podium --------------------------------------------------------------------------------------

/** Final ranks from match scores: index 0 = champion slot (ties by slot), like the spawn order. */
export function podiumRanks(ctx: Ctx, out: number[]): number[] {
  out.length = 0;
  for (const b of ctx.w.beans) if (b.active) out.push(b.slot);
  const score = (s: number): number => ctx.w.cfg.matchScores[s] ?? 0;
  out.sort((a, b) => score(b) - score(a) || a - b);
  return out;
}

const ranks: number[] = [];

export function podiumOptions(ctx: Ctx, c: Choice): number {
  const brain = ctx.brain;
  const b = ctx.self;
  podiumRanks(ctx, ranks);
  const me = ranks.indexOf(b.slot);
  const champ = ranks[0];
  if (me === 0) {
    consider(ctx, c, Goal.PODIUM, NO_SLOT, 0, MT.podium.defendU);
    return MT.podium.attackMult;
  }
  if (me > 0 && me < 3 && ctx.w.tick < brain.lobbyUntil) {
    consider(ctx, c, Goal.PODIUM, NO_SLOT, 0, MT.podium.defendU);
    return MT.podium.attackMult;
  }
  if (me > 0 && me < 3 && ctx.w.tick >= brain.lobbyUntil) brain.lobbyUntil = ctx.w.tick + randIntRange(brain, MT.podium.standMinTicks, MT.podium.standMaxTicks) * (chance(brain, MT.podium.joinChance) ? 0 : 1);
  if (champ !== undefined && champ !== b.slot && ctx.seen[champ].body) consider(ctx, c, Goal.PODIUM, champ, 0, MT.podium.dethroneU);
  return MT.podium.attackMult;
}

/** Podium play: defend your pedestal, or jump up and shove the champion off (§6.6, §10.4). */
export function podium(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const spawns = MAPS[MapId.PODIUM].spawns;
  podiumRanks(ctx, ranks);
  const me = ranks.indexOf(b.slot);
  if (brain.targetEnt === 0 && brain.target === NO_SLOT && me >= 0 && me < 3) {
    // Defend: stay on the pedestal, shove climbers.
    const home = spawns[me];
    const close = nearestEnemy(ctx, MT.podium.defendShoveRange);
    // Only bop someone coming up (on the pedestal or mid-jump): a lunge at the crowd below just
    // carries the defender off its own pedestal.
    const onTop = b.groundZ >= MT.podium.pedestalMinZ;
    const rising = close !== NO_SLOT && ctx.seen[close].z > b.groundZ - MT.podium.climberDz;
    if (close !== NO_SLOT && b.cdShove === 0 && b.state === BS.NORMAL && hittable(ctx, close) && (!onTop || rising)) {
      shoveAt(ctx, ctx.seen[close], ctx.seen[close].x, ctx.seen[close].y);
      return;
    }
    const away = Math.hypot(home.x - b.pos.x, home.y - b.pos.y);
    if (away > MT.podium.homeSlack && b.groundZ < MT.podium.pedestalMinZ) {
      // Knocked off: run back and hop up (in the air, keep driving at it: air control is weak).
      if (grounded(b)) podiumClimb(ctx, home.x, home.y);
      else {
        wantDir(ctx, home.x - b.pos.x, home.y - b.pos.y, 1);
        brain.want.precise = true;
        brain.want.margin = 0;
      }
      return;
    }
    wantPoint(ctx, home.x, home.y, MT.podium.homeSpeed);
    brain.want.precise = true;
    brain.want.margin = 0;
    // King of the hill: standing proud on the pedestal is the point (and sometimes a little dance).
    if (b.groundZ >= MT.podium.pedestalMinZ) {
      brain.waiting = true;
      if (brain.emoteAt < 0 && chance(brain, MT.podium.showOffChance)) {
        brain.emoteAt = w.tick + randIntRange(brain, 10, 40);
        brain.emoteId = 1 + randIntRange(brain, 0, 3);
      }
    }
    return;
  }
  const champ = ranks[0];
  if (champ === undefined) {
    wander(ctx);
    return;
  }
  const o = ctx.seen[champ];
  const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
  if (b.cdShove === 0 && (b.state === BS.NORMAL) && d < MT.podium.shoveRange && Math.abs(o.z - b.z) <= TUNE.shove.maxDz && hittable(ctx, champ)) {
    shoveAt(ctx, o, o.x, o.y);
    return;
  }
  podiumClimb(ctx, spawns[0].x, spawns[0].y);
  void w;
}

/**
 * Runs at a pedestal and jumps from the right distance to land on top (§6.6: the apex clears the
 * tallest one). Too close for a clean jump: back off for a run-up first.
 */
function podiumClimb(ctx: Ctx, px: number, py: number): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const dx = px - b.pos.x;
  const dy = py - b.pos.y;
  const d = Math.max(1e-6, Math.hypot(dx, dy));
  const ux = dx / d;
  const uy = dy / d;
  const ped = MT.podium;
  brain.want.margin = 0;
  brain.want.precise = true;
  const v = b.vel.x * ux + b.vel.y * uy;
  // Too close to reach jump speed before the take-off window: back off for a run-up.
  const tooClose = d < ped.jumpMin || (v < ped.jumpSpeed && d < ped.jumpFrom + ped.accelRoom);
  if (grounded(b) && b.groundZ < ped.pedestalMinZ && tooClose) {
    wantPoint(ctx, px - ux * ped.runUpFrom, py - uy * ped.runUpFrom, 1);
    brain.want.arrive = 0.25;
    return;
  }
  if (grounded(b) && b.groundZ < ped.pedestalMinZ && d <= ped.jumpFrom && v >= ped.jumpSpeed && b.state === BS.NORMAL) brain.press.jump = true;
  wantDir(ctx, ux, uy, 1);
}

// ---- Flavor goals ---------------------------------------------------------------------------------

/** Victory dance near an edge after a KO (§10.6 fun rules): punishable on purpose. */
export function dance(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  if (w.tick >= brain.danceUntil) {
    brain.forceThink = true;
    wander(ctx);
    return;
  }
  if (w.tick - brain.pointTick > MT.dance.repickTicks) {
    // A spot near the edge, close by.
    let best = -1e9;
    for (let i = 0; i < MT.dance.candidates; i++) {
      const a = rand(brain) * Math.PI * 2;
      const r = MT.dance.searchRadius * rand(brain);
      const x = b.pos.x + dcos(a) * r;
      const y = b.pos.y + dsin(a) * r;
      const s = ctx.field.safety(x, y);
      const score = -Math.abs(s - MT.dance.edgeSafety) - r * MT.dance.travelWeight;
      if (score > best) {
        best = score;
        brain.gx = x;
        brain.gy = y;
      }
    }
    brain.pointTick = w.tick;
  }
  const d = Math.hypot(brain.gx - b.pos.x, brain.gy - b.pos.y);
  if (d > MT.dance.arrive) {
    wantPoint(ctx, brain.gx, brain.gy, MT.dance.walkSpeed);
    brain.want.margin = MT.dance.edgeSafety * 0.5;
    brain.want.precise = true;
    return;
  }
  // The dance: little wiggles in place and an emote.
  brain.waiting = true;
  const a = w.tick * MT.dance.wiggleRate;
  wantDir(ctx, dcos(a), dsin(a), MT.dance.wiggleSpeed);
  brain.want.margin = MT.dance.edgeSafety * 0.5;
  brain.want.precise = true;
  if (brain.emoteAt < 0 && (w.tick % MT.dance.emoteEvery) === 0) brain.emoteAt = w.tick;
}

/** Gary's teacup ride: run in at the right speed and hop into the spinning cup. */
export function cupRide(ctx: Ctx): void {
  const brain = ctx.brain;
  const b = ctx.self;
  const C = MT.cup;
  const r = Math.hypot(b.pos.x, b.pos.y);
  if (b.state === BS.SPUN || ctx.w.tick - brain.goalSince > C.maxTicks || ctx.w.map.cupOccupant !== NO_SLOT) {
    brain.forceThink = true;
    wander(ctx);
    return;
  }
  const ux = r > 1e-6 ? -b.pos.x / r : 1;
  const uy = r > 1e-6 ? -b.pos.y / r : 0;
  if (r > C.runUpRadius + C.runUpSlack && grounded(b)) {
    wantPoint(ctx, -ux * C.runUpRadius, -uy * C.runUpRadius, 1);
    return;
  }
  wantDir(ctx, ux, uy, C.runStick);
  brain.want.precise = true;
  brain.want.margin = 0;
  if (grounded(b) && r <= C.jumpRadius && b.state === BS.NORMAL) brain.press.jump = true;
}

